import { useTranslation } from 'react-i18next';
import { Badge, Box, Card, Group, Stack, Table, Text } from '@mantine/core';
import BestPriceCell from '@/components/app/buy/BestPriceCell';
import SectionLabel from '@/components/common/SectionLabel';
import { makeOfferUrl } from '@/lib/buyLinks';
import { formatMoney, formatProperty } from '@/lib/format';
import type {
  BestPriceByOrder,
  GoodsProperty,
  PrepaymentKey,
  PurchaseGoodsItem,
  PurchasePriceList,
  QualityRow,
} from '@/types/buy';
import numClasses from '@/components/common/NumCell.module.css';

function price(row: QualityRow, key: PrepaymentKey): number | null | undefined {
  return row.pricesByPrepaymentMap?.[key];
}

// Свойства качества — чипами (как параметры приёмки на старом портале): заметно и сканируемо.
function QualityChips({ properties }: { readonly properties: readonly GoodsProperty[] }) {
  return (
    <Group gap={4}>
      {properties.map((property) => (
        <Badge key={property.name} variant="default" tt="none" fw={500} size="lg" radius="sm" c="gray.9">
          {formatProperty(property)}
        </Badge>
      ))}
    </Group>
  );
}

interface PriceGridProps {
  readonly list: PurchasePriceList;
  /** Лучшие цены продажи по orderId строк (Фаза 2); undefined — рынок не запрашивался. */
  readonly bestPrices?: BestPriceByOrder;
  readonly bestPricesLoading?: boolean;
  readonly bestPricesError?: boolean;
}

// Грид цен (read-only): по каждому товару (классу) — строки качества × колонки предоплаты:
// моя цена (крупно) + бейдж «рынок» под ней. Редактирование — Фаза 3, «Купить» — Фаза 2b.
// Десктоп — таблица с брендовой шапкой, мобайл — карточки.
export default function PriceGrid({ list, bestPrices, bestPricesLoading, bestPricesError }: PriceGridProps) {
  const { t } = useTranslation();

  // Ячейка предоплаты: моя цена + рынок (с кнопкой «Купить за X», когда предложение есть).
  // Строки без orderId (шаблонные) рынка не имеют.
  const renderMarket = (item: PurchaseGoodsItem, row: QualityRow, key: PrepaymentKey) => {
    if (row.orderId == null) {
      return null;
    }
    const entry = bestPrices?.get(row.orderId)?.[key];
    const myPrice = price(row, key);
    // Выгода для buy: купить дешевле моей цены = моя − рыночная (>0 — выгодно). Прокидываем и в ссылку,
    // чтобы превью оффера показало «к вашей цене».
    const advantage = entry && myPrice != null ? myPrice - entry.price : undefined;
    return (
      <BestPriceCell
        entry={entry}
        myPrice={myPrice}
        loading={bestPricesLoading}
        unavailable={bestPricesError}
        buyTo={
          entry
            ? makeOfferUrl({
                entry,
                fromPurchaseOrderId: row.orderId,
                toAddress: list.address,
                toAddressTitle: list.addressTitle,
                goods: item.parentGoodsType,
                year: item.cropYear,
                advantage,
              })
            : undefined
        }
      />
    );
  };

  return (
    <Stack gap="xl">
      {list.goodsList.map((item) => (
        <Stack key={`${item.goodsType}-${item.cropYear}`} gap="sm">
          {/* Класс уточняет заголовок страницы («Пшеница 3 класс»); без классов имя культуры
              дублировало бы заголовок — пишем «Цены». */}
          <SectionLabel>
            {item.goodsType === item.parentGoodsType ? t('app.buy.pricesLabel') : item.goodsType}
          </SectionLabel>

          <Box visibleFrom="sm">
            <Table
              withTableBorder
              verticalSpacing="sm"
              highlightOnHover
              highlightOnHoverColor="lime.0"
            >
              <Table.Thead bg="brand.0">
                <Table.Tr>
                  <Table.Th c="brand.9" w="32%">
                    {t('app.buy.quality')}
                  </Table.Th>
                  <Table.Th c="brand.9" ta="right">
                    {t('app.buy.prepayment0')}
                  </Table.Th>
                  <Table.Th c="brand.9" ta="right">
                    {t('app.buy.prepayment100')}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {item.qualityRows.map((row) => (
                  <Table.Tr key={row.propsSetId}>
                    <Table.Td>
                      <QualityChips properties={row.properties} />
                    </Table.Td>
                    {(['0', '100'] as const).map((key) => (
                      <Table.Td key={key} ta="right">
                        {/* Лучшая цена — ПЕРЕД моей, на одной линии (как в карточках «Продам»). */}
                        <Group gap="sm" wrap="nowrap" justify="flex-end" align="center">
                          {renderMarket(item, row, key)}
                          <Text fw={700} className={numClasses.num} style={{ flexShrink: 0 }}>
                            {formatMoney(price(row, key))}
                          </Text>
                        </Group>
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>

          <Stack gap="sm" hiddenFrom="sm">
            {item.qualityRows.map((row) => (
              <Card key={row.propsSetId} withBorder padding="sm">
                <QualityChips properties={row.properties} />
                {(['0', '100'] as const).map((key) => (
                  <Group key={key} justify="space-between" wrap="nowrap" mt={8} gap="sm">
                    <Text size="sm" c="gray.7" style={{ flexShrink: 0 }}>
                      {t(key === '0' ? 'app.buy.prepayment0' : 'app.buy.prepayment100')}
                    </Text>
                    {/* Лучшая цена — ПЕРЕД моей, на одной линии (как в карточках «Продам»). */}
                    <Group gap="sm" wrap="nowrap" justify="flex-end" align="center" style={{ minWidth: 0 }}>
                      {renderMarket(item, row, key)}
                      <Text fw={700} style={{ flexShrink: 0 }}>
                        {formatMoney(price(row, key))}
                      </Text>
                    </Group>
                  </Group>
                ))}
              </Card>
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}
